/**
 * Display helpers for the timeline place card: scheduled-time pill and "Open until …" status.
 */

import { format } from 'date-fns';
import { parseLocalDate } from '@/utils/dateUtils';

const MINUTES_PER_DAY = 24 * 60;
const MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY;

function parseHhMm(value: string): { hour: number; minute: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return { hour, minute };
}

function clockLabel(hour: number, minute: number, withPeriod: boolean, dropZeroMinutes = false): string {
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const time = dropZeroMinutes && minute === 0 ? `${hour12}` : `${hour12}:${String(minute).padStart(2, '0')}`;
  return withPeriod ? `${time} ${hour < 12 ? 'AM' : 'PM'}` : time;
}

/** "10:00" → "10:00 AM", "13:30" → "1:30 PM"; anything else unchanged. */
export function formatClockTime(value: string): string {
  const parsed = parseHhMm(value);
  return parsed ? clockLabel(parsed.hour, parsed.minute, true) : value;
}

/**
 * A hotel's check-in / check-out, stored in the stop's time field as "YYYY-MM-DD/YYYY-MM-DD".
 * Both may be the same day when only one date was picked.
 */
export interface StayDates {
  checkIn: string;
  checkOut: string;
}

const STAY_DATES_PATTERN = /^(\d{4}-\d{2}-\d{2})\/(\d{4}-\d{2}-\d{2})$/;

/** Stay dates from a time field, or null when it holds a time range (or nothing valid). */
export function parseStayDates(value: string | null | undefined): StayDates | null {
  const match = value ? STAY_DATES_PATTERN.exec(value.trim()) : null;
  if (!match) return null;
  const checkIn = parseLocalDate(match[1]);
  const checkOut = parseLocalDate(match[2]);
  if (!checkIn || !checkOut || checkOut < checkIn) return null;
  return { checkIn: match[1], checkOut: match[2] };
}

export function formatStayDatesValue({ checkIn, checkOut }: StayDates): string {
  return `${checkIn}/${checkOut}`;
}

/** "Nov 2 – Nov 5", or "Nov 2" for a single date; the year is added when the stay crosses into another year. */
function formatStayDatesLabel({ checkIn, checkOut }: StayDates): string {
  const start = parseLocalDate(checkIn)!;
  const end = parseLocalDate(checkOut)!;
  if (checkIn === checkOut) return format(start, 'MMM d');
  const pattern = start.getFullYear() === end.getFullYear() ? 'MMM d' : 'MMM d, yyyy';
  return `${format(start, pattern)} – ${format(end, pattern)}`;
}

/**
 * A hotel's stay on a trip counted in days (no calendar dates): its first and last day,
 * 1-based, stored in the stop's time field as "days:1-3".
 */
export interface StayDays {
  firstDay: number;
  lastDay: number;
}

const STAY_DAYS_PATTERN = /^days:(\d+)-(\d+)$/;

export function parseStayDays(value: string | null | undefined): StayDays | null {
  const match = value ? STAY_DAYS_PATTERN.exec(value.trim()) : null;
  if (!match) return null;
  const firstDay = Number(match[1]);
  const lastDay = Number(match[2]);
  if (firstDay < 1 || lastDay < firstDay) return null;
  return { firstDay, lastDay };
}

export function formatStayDaysValue({ firstDay, lastDay }: StayDays): string {
  return `days:${firstDay}-${lastDay}`;
}

/** "Day 1 – Day 3", or "Day 2" for a single day. */
export function formatStayDaysLabel({ firstDay, lastDay }: StayDays): string {
  return firstDay === lastDay ? `Day ${firstDay}` : `Day ${firstDay} – Day ${lastDay}`;
}

/**
 * "10:00-11:30" → "10:00 – 11:30 AM"; "11:00-13:00" → "11:00 AM – 1:00 PM";
 * hotel stay dates "2026-11-02/2026-11-05" → "Nov 2 – Nov 5"; stay days "days:1-3" → "Day 1 – Day 3".
 * Anything else is returned unchanged.
 */
export function formatTimeRangeLabel(value: string | null | undefined): string {
  if (!value) return '';
  const stayDates = parseStayDates(value);
  if (stayDates) return formatStayDatesLabel(stayDates);
  const stayDays = parseStayDays(value);
  if (stayDays) return formatStayDaysLabel(stayDays);
  const parts = value.split('-');
  if (parts.length !== 2) return value;
  const start = parseHhMm(parts[0]);
  const end = parseHhMm(parts[1]);
  if (!start || !end) return value;
  const samePeriod = start.hour < 12 === end.hour < 12;
  return `${clockLabel(start.hour, start.minute, !samePeriod)} – ${clockLabel(end.hour, end.minute, true)}`;
}

export interface PlaceOpenStatus {
  isOpen: boolean;
  label: string;
}

type PeriodPoint = { day: number; minutes: number };

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
}

/** Places (New) `{ day, hour, minute }` or legacy `{ day, time: "0930" }`. */
function readPeriodPoint(value: unknown): PeriodPoint | null {
  const point = asRecord(value);
  if (!point || typeof point.day !== 'number') return null;
  if (typeof point.hour === 'number') {
    const minute = typeof point.minute === 'number' ? point.minute : 0;
    return { day: point.day, minutes: point.hour * 60 + minute };
  }
  if (typeof point.time === 'string' && /^\d{4}$/.test(point.time)) {
    return { day: point.day, minutes: Number(point.time.slice(0, 2)) * 60 + Number(point.time.slice(2)) };
  }
  return null;
}

function readPeriods(gpd: Record<string, unknown>): unknown[] | null {
  for (const key of ['regularOpeningHours', 'regular_opening_hours', 'opening_hours', 'openingHours']) {
    const periods = asRecord(gpd[key])?.periods;
    if (Array.isArray(periods) && periods.length > 0) return periods;
  }
  return null;
}

function readUtcOffsetMinutes(gpd: Record<string, unknown>): number | null {
  for (const key of ['utcOffsetMinutes', 'utc_offset_minutes', 'utc_offset']) {
    if (typeof gpd[key] === 'number') return gpd[key] as number;
  }
  return null;
}

/**
 * "Open until 11 PM" / "Closed now" at the place's local time, from Google opening-hour periods.
 * Returns null without periods or a UTC offset: the viewer's clock would give the wrong answer.
 */
export function getPlaceOpenStatus(
  googlePlacesData: Record<string, unknown> | null | undefined,
  now: Date = new Date(),
): PlaceOpenStatus | null {
  if (!googlePlacesData) return null;
  const periods = readPeriods(googlePlacesData);
  const utcOffset = readUtcOffsetMinutes(googlePlacesData);
  if (!periods || utcOffset === null) return null;

  // Google's "always open": one period opening Sunday 00:00 with no close.
  const first = asRecord(periods[0]);
  if (periods.length === 1 && first && !first.close) {
    const open = readPeriodPoint(first.open);
    if (open && open.day === 0 && open.minutes === 0) return { isOpen: true, label: 'Open 24 hours' };
  }

  const placeNow = new Date(now.getTime() + utcOffset * 60_000);
  const nowInWeek = placeNow.getUTCDay() * MINUTES_PER_DAY + placeNow.getUTCHours() * 60 + placeNow.getUTCMinutes();

  for (const period of periods) {
    const record = asRecord(period);
    const open = readPeriodPoint(record?.open);
    const close = readPeriodPoint(record?.close);
    if (!open || !close) continue;
    const openAt = open.day * MINUTES_PER_DAY + open.minutes;
    let closeAt = close.day * MINUTES_PER_DAY + close.minutes;
    if (closeAt <= openAt) closeAt += MINUTES_PER_WEEK;
    const isInside = (t: number) => t >= openAt && t < closeAt;
    if (isInside(nowInWeek) || isInside(nowInWeek + MINUTES_PER_WEEK)) {
      const hour = Math.floor(close.minutes / 60);
      return { isOpen: true, label: `Open until ${clockLabel(hour, close.minutes % 60, true, true)}` };
    }
  }
  return { isOpen: false, label: 'Closed now' };
}

/** "Jessica Smith" → "Jessica’s Tip"; no name → "Creator’s Tip". */
export function creatorTipLabel(name: string | null | undefined): string {
  const firstName = (name ?? '').trim().split(/\s+/)[0];
  return `${firstName || 'Creator'}’s Tip`;
}

export interface PlaceNoteAuthor {
  name?: string | null;
  avatarUrl?: string | null;
}

/** Note author from a post's `user` (or the signed-in user): name plus the smallest avatar. */
export function noteAuthorFromUser(user: unknown): PlaceNoteAuthor | undefined {
  const record = asRecord(user);
  if (!record) return undefined;
  const pick = (key: string) => (typeof record[key] === 'string' && record[key] ? (record[key] as string) : null);
  const name = pick('name');
  const avatarUrl =
    pick('small_avatar_url') || pick('large_avatar_url') || pick('avatar_url') || pick('profile_image_url');
  return name || avatarUrl ? { name, avatarUrl } : undefined;
}
